import type { SchemaInput } from './schema-input.js';
import { isSchemaRecord, isString } from './runtime-values.js';
import { CollaborationSaveError, type CollaborationSaveCode } from './collaboration-save-error.js';

type ExpectedRequestOutcome = 'signin_expired' | 'signin_required' | 'plan_required' | 'input_required';

function expectedRequestOutcome(operation: string, status: number, error: SchemaInput): ExpectedRequestOutcome | undefined {
  if (!isString(error)) return undefined;
  switch (`${operation}|${status}|${error}`) {
    case 'auth.google.signin|403|Sign-in expired. Please try again.': return 'signin_expired';
    case 'maps.delete|401|Unauthorized': return 'signin_required';
    case 'maps.visibility|403|Pro required': return 'plan_required';
    case 'maps.visibility|400|Password required': return 'input_required';
    default: return undefined;
  }
}

export class HttpRequestError extends Error {
  readonly httpStatus: number;
  readonly operation: string;
  readonly requestId?: string;
  readonly expectedOutcome?: ExpectedRequestOutcome;

  constructor(message: string, response: Pick<Response, 'status'> & Partial<Pick<Response, 'headers'>>, operation: string, expectedOutcome?: ExpectedRequestOutcome) {
    super(message);
    this.name = 'HttpRequestError';
    this.httpStatus = response.status;
    this.operation = operation;
    this.expectedOutcome = expectedOutcome;
    const id = response.headers?.get('x-request-id');
    if (id && /^[a-f0-9-]{36}$/i.test(id)) this.requestId = id;
  }

  static async fromResponse(message: string, response: Response, operation: string): Promise<HttpRequestError> {
    const body: unknown = await response.clone().json().catch(() => null);
    const outcome = expectedRequestOutcome(operation, response.status, isSchemaRecord(body) ? body.error : undefined);
    return new HttpRequestError(message, response, operation, outcome);
  }
}

export interface RequestFailureContext {
  service: 'wikidata' | 'wikipedia' | 'account-analytics';
  stage: 'fetch' | 'decode' | 'apply';
  page_path?: string;
  language?: string;
  request_trigger?: 'details' | 'retry';
  elapsed_ms?: number;
  online?: boolean;
  visibility?: string;
  response_received?: boolean;
  http_status?: number;
  response_type?: string;
}

interface RequestErrorDetails {
  failure_code?: CollaborationSaveCode;
  expected_outcome?: ExpectedRequestOutcome;
  operation?: string;
  http_status?: number;
  request_id?: string;
  failure_kind?: 'http' | 'timeout' | 'aborted' | 'fetch' | 'invalid_json' | 'body_read' | 'client';
  cause_type?: string;
}

export function requestErrorDetails(error: SchemaInput): RequestErrorDetails {
  const seen = new Set<Error>();
  let current = error;
  while (current instanceof Error && !seen.has(current) && seen.size < 5) {
    seen.add(current);
    if (current instanceof CollaborationSaveError && ['data_layers_subscription', 'service_area_subscription'].includes(current.code)) {
      return { operation: 'maps.collaboration.save', expected_outcome: 'plan_required', failure_code: current.code };
    }
    if (current instanceof HttpRequestError) {
      const details: RequestErrorDetails = { operation: current.operation, http_status: current.httpStatus, failure_kind: 'http' };
      if (current.requestId) details.request_id = current.requestId;
      if (current.expectedOutcome) details.expected_outcome = current.expectedOutcome;
      return details;
    }
    if (current.name === 'TimeoutError') return { failure_kind: 'timeout', cause_type: 'TimeoutError' };
    if (current.name === 'AbortError') return { failure_kind: 'aborted', cause_type: 'AbortError' };
    if (current instanceof TypeError && !current.cause) return { cause_type: 'TypeError' };
    current = current.cause;
  }
  return {};
}

export function stagedRequestErrorDetails(error: SchemaInput, context: RequestFailureContext): RequestErrorDetails & RequestFailureContext {
  const details = requestErrorDetails(error);
  const failureKind = details.failure_kind ?? (context.stage === 'fetch' ? 'fetch'
    : context.stage === 'decode' ? (error instanceof SyntaxError ? 'invalid_json' : 'body_read')
      : 'client');
  return { ...details, ...context, failure_kind: failureKind };
}
